import { observer } from 'mobx-react-lite'
import { type ScopeChangeRequestsViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestsViewModel'
import { ScopeChangeRequests } from '@/features/admin/scope-change-requests/ScopeChangeRequests'
import { useDocumentTitle } from '@/hooks/document-title'
import { useActivation } from '@/hooks/view-model-hooks'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { WorkspaceFilterViewHeadingContainer } from '@/components/WorkspaceFilterViewHeadingContainer'
import { ScopeChangeRequestStatusFilterContainer } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestStatusFilter/ScopeChangeRequestStatusFilterContainer'
import { ScopeChangeRequestTableContainer } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestTable/ScopeChangeRequestTableContainer'
import { ScopeChangeRequestDetailsPanelContainer } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ScopeChangeRequestDetailsPanelContainer'
import { ScopeChangeRequestFinalizedDialogContainer } from '@/features/admin/scope-change-requests/ScopeChangeRequestFinalizedDialog/ScopeChangeRequestFinalizedDialogContainer'
import { ScopeChangeRequestResolveDialogContainer } from '@/features/admin/scope-change-requests/ScopeChangeRequestResolveDialog/ScopeChangeRequestResolveDialogContainer'
import { ScopeChangeRequestRejectDialogContainer } from '@/features/admin/scope-change-requests/ScopeChangeRequestRejectDialog/ScopeChangeRequestRejectDialogContainer'
import { ScopeChangeEditCustomItemsDialogContainer } from '@/features/admin/scope-change-requests/ScopeChangeEditCustomItemsDialog/ScopeChangeEditCustomItemsDialogContainer'
import { ScopeChangeEditInventoryItemsDialogContainer } from '@/features/admin/scope-change-requests/ScopeChangeEditInventoryItemsDialog/ScopeChangeEditInventoryItemsDialogContainer'
import { ScopeChangeRequestActionSelectionDialogContainer } from '@/features/admin/scope-change-requests/ScopeChangeRequestActionSelectionDialog/ScopeChangeRequestActionSelectionDialogContainer'

export interface ScopeChangeRequestsContainerProps {
  vm: ScopeChangeRequestsViewModel
}

export const ScopeChangeRequestsContainer = observer(
  function ScopeChangeRequestsContainer({
    vm,
  }: ScopeChangeRequestsContainerProps) {
    useDocumentTitle('Change Requests')
    useActivation(vm)

    return vm.fetch.match({
      pending: () => <ContentLoading />,
      rejected: (err) => <ContentError error={err} />,
      resolved: () => <ScopeChangeRequestsContentContainer vm={vm} />,
    })
  },
)

const ScopeChangeRequestsContentContainer = observer(
  function ScopeChangeRequestsContentContainer({
    vm,
  }: ScopeChangeRequestsContainerProps) {
    return (
      <ScopeChangeRequests
        heading={
          <WorkspaceFilterViewHeadingContainer
            allOptionText="All Workspaces"
            primaryText="Change requests"
            filter={vm.searcher.workspaceFilter}
            testId="workspace-filter-view-heading-dropdown"
          />
        }
        isSideSheetVisible={vm.changeRequestDetailsVm.modal.showing}
        statusFilter={
          <ScopeChangeRequestStatusFilterContainer
            filter={vm.searcher.statusFilter}
          />
        }
        searchText={vm.searcher.searchText.value}
        onSearchTextChange={vm.searcher.searchText.change}
        table={<ScopeChangeRequestTableContainer vm={vm} />}
        detailsPanel={
          <ScopeChangeRequestDetailsPanelContainer
            vm={vm.changeRequestDetailsVm}
          />
        }
      >
        <ScopeChangeEditCustomItemsDialogContainer
          vm={vm.changeRequestDetailsVm.scopeChangeEditCustomItemsDialogVm}
        />
        <ScopeChangeEditInventoryItemsDialogContainer
          vm={vm.changeRequestDetailsVm.scopeChangeEditInventoryItemsDialogVm}
        />
        <ScopeChangeRequestFinalizedDialogContainer
          vm={vm.scopeChangeRequestFinalizedDialogVm}
        />
        <ScopeChangeRequestActionSelectionDialogContainer
          vm={
            vm.changeRequestDetailsVm.scopeChangeRequestActionSelectionDialogVm
          }
        />
        <ScopeChangeRequestResolveDialogContainer
          vm={vm.scopeChangeRequestResolveDialogVm}
        />
        <ScopeChangeRequestRejectDialogContainer
          vm={vm.scopeChangeRequestRejectDialogVm}
        />
        <ScopeChangeRequestRejectDialogContainer
          vm={vm.scopeChangeRequestRejectOnBehalfOfClientDialogVm}
        />
      </ScopeChangeRequests>
    )
  },
)
